import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  ContentPlatformDefinition,
  PlatformPublishResult,
  getContentPlatforms,
  getSyndicationCredentials,
  publishSyndicatedContent,
} from '../../services/contentSyndicationService';

const categoryLabel: Record<ContentPlatformDefinition['category'], string> = {
  blog: 'Blog / Article',
  traffic: 'Traffic + Backlink',
  seo: 'SEO + Social',
  custom: 'Custom',
};

const automationLabel: Record<ContentPlatformDefinition['automationLevel'], { label: string; color: 'success' | 'warning' | 'default' }> = {
  direct_api: { label: 'Direct API', color: 'success' },
  oauth_or_token: { label: 'OAuth / Token', color: 'warning' },
  not_available: { label: 'Manual Only', color: 'default' },
};

interface ContentSyndicationPanelProps {
  onOpenCredentialsSettings?: () => void;
}

const ContentSyndicationPanel: React.FC<ContentSyndicationPanelProps> = ({ onOpenCredentialsSettings }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [platforms, setPlatforms] = useState<ContentPlatformDefinition[]>([]);
  const [loadingPlatforms, setLoadingPlatforms] = useState(false);
  const [platformError, setPlatformError] = useState<string | null>(null);
  const [savedCredentials, setSavedCredentials] = useState<Record<string, Record<string, string>>>({});

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [canonicalUrl, setCanonicalUrl] = useState('');
  const [tagsInput, setTagsInput] = useState('');

  const [selectedPlatformIds, setSelectedPlatformIds] = useState<string[]>([]);

  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishResults, setPublishResults] = useState<PlatformPublishResult[]>([]);
  const [summary, setSummary] = useState<{ requested: number; published: number; failed: number; skipped: number } | null>(null);

  const groupedPlatforms = useMemo(() => {
    return platforms.reduce<Record<string, ContentPlatformDefinition[]>>((acc, platform) => {
      const key = platform.category;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(platform);
      return acc;
    }, {});
  }, [platforms]);

  const borderColor = theme.palette.divider;
  const mutedColor = theme.palette.text.secondary;
  const platformCardBg = alpha(theme.palette.text.primary, isDark ? 0.08 : 0.03);
  const sectionShadow = isDark ? '0 10px 30px rgba(0,0,0,0.35)' : '0 4px 20px -5px rgba(0,0,0,0.05)';

  useEffect(() => {
    const loadPlatforms = async () => {
      setLoadingPlatforms(true);
      setPlatformError(null);
      try {
        const [platformData, credentialsData] = await Promise.all([
          getContentPlatforms(),
          getSyndicationCredentials(),
        ]);
        setPlatforms(platformData);
        setSavedCredentials(credentialsData || {});
      } catch (error) {
        console.error('Failed to load content platforms', error);
        setPlatformError('Failed to load publishing platforms. Please refresh.');
      } finally {
        setLoadingPlatforms(false);
      }
    };

    loadPlatforms();
  }, []);

  const handlePlatformToggle = (platformId: string, checked: boolean) => {
    setSelectedPlatformIds((prev) => {
      if (checked) {
        if (prev.includes(platformId)) return prev;
        return [...prev, platformId];
      }
      return prev.filter((id) => id !== platformId);
    });
  };

  const handleSelectApiReady = () => {
    const readyIds = platforms.filter((platform) => platform.supportsBulkPublish).map((platform) => platform.id);
    setSelectedPlatformIds(readyIds);
  };

  const isPlatformConfigured = (platform: ContentPlatformDefinition): boolean => {
    const requiredFields = platform.requiredFields.filter((field) => field.required);
    if (requiredFields.length === 0) return true;
    const creds = savedCredentials[platform.id] || {};
    return requiredFields.every((field) => Boolean((creds[field.key] || '').trim()));
  };

  const handleSubmit = async () => {
    setPublishError(null);
    setPublishResults([]);
    setSummary(null);

    const trimmedTitle = title.trim();
    const trimmedContent = content.trim();

    if (!trimmedTitle || !trimmedContent) {
      setPublishError('Title and content are required.');
      return;
    }

    if (selectedPlatformIds.length === 0) {
      setPublishError('Select at least one platform.');
      return;
    }

    const missingConfiguredPlatforms = selectedPlatformIds
      .map((platformId) => platforms.find((platform) => platform.id === platformId))
      .filter((platform): platform is ContentPlatformDefinition => Boolean(platform))
      .filter((platform) => platform.supportsBulkPublish && !isPlatformConfigured(platform))
      .map((platform) => platform.name);

    if (missingConfiguredPlatforms.length > 0) {
      setPublishError(
        `Credentials missing for: ${missingConfiguredPlatforms.join(', ')}. Please update in Settings > Syndication Credentials.`
      );
      return;
    }

    const tags = tagsInput
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

    setPublishing(true);
    try {
      const response = await publishSyndicatedContent({
        title: trimmedTitle,
        content: trimmedContent,
        canonicalUrl: canonicalUrl.trim() || undefined,
        tags,
        selectedPlatformIds,
        platformCredentials: {},
      });
      setPublishResults(response.results);
      setSummary(response.summary);
    } catch (error) {
      console.error('Failed to publish content', error);
      setPublishError('Publishing failed. Please review credentials and try again.');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Content Syndication Automation</Typography>
        <Typography variant="body2" color={mutedColor} sx={{ mt: 0.5 }}>
          Ek article likho, select platforms choose karo, aur single click se multi-platform publish trigger karo.
        </Typography>
        <Typography variant="body2" color={mutedColor} sx={{ mt: 0.5 }}>
          Saved credentials Settings ke Syndication Credentials page se automatically use ho jayenge.
        </Typography>
      </Box>

      <Card sx={{ borderRadius: 3, border: `1px solid ${borderColor}`, boxShadow: sectionShadow, mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={2}>
            <TextField
              label="Article Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Canonical URL (optional, recommended for backlinks)"
              value={canonicalUrl}
              onChange={(e) => setCanonicalUrl(e.target.value)}
              fullWidth
            />
            <TextField
              label="Tags (comma separated)"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              fullWidth
              placeholder="seo, ai, exam-preparation"
            />
            <TextField
              label="Article Content (Markdown allowed)"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              fullWidth
              required
              multiline
              minRows={10}
            />
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3, border: `1px solid ${borderColor}`, boxShadow: sectionShadow, mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>Platforms</Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                onClick={onOpenCredentialsSettings}
                disabled={!onOpenCredentialsSettings}
              >
                Manage Credentials
              </Button>
              <Button variant="outlined" size="small" onClick={handleSelectApiReady}>
                Select API-ready
              </Button>
              <Button variant="text" size="small" color="inherit" onClick={() => setSelectedPlatformIds([])}>
                Clear Selection
              </Button>
            </Stack>
          </Box>

          {loadingPlatforms && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {platformError && (
            <Alert severity="error">{platformError}</Alert>
          )}

          {!loadingPlatforms && !platformError && (
            <Stack spacing={2.5}>
              {Object.entries(groupedPlatforms).map(([category, list]) => (
                <Box key={category}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                    {categoryLabel[category as ContentPlatformDefinition['category']]}
                  </Typography>
                  <Stack spacing={2}>
                    {list.map((platform) => {
                      const isChecked = selectedPlatformIds.includes(platform.id);
                      const automation = automationLabel[platform.automationLevel];
                      const isConfigured = isPlatformConfigured(platform);
                      return (
                        <Card
                          key={platform.id}
                          sx={{ borderRadius: 2, border: `1px solid ${borderColor}`, bgcolor: platformCardBg }}
                        >
                          <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                              <Box>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={isChecked}
                                      onChange={(e) => handlePlatformToggle(platform.id, e.target.checked)}
                                      disabled={!platform.supportsBulkPublish}
                                    />
                                  }
                                  label={<Typography fontWeight={700}>{platform.name}</Typography>}
                                />
                                <Typography variant="body2" color={mutedColor} sx={{ mt: 0.5 }}>
                                  {platform.description}
                                </Typography>
                                {platform.website && (
                                  <Link href={platform.website} target="_blank" rel="noreferrer" sx={{ fontSize: 13 }}>
                                    {platform.website}
                                  </Link>
                                )}
                              </Box>
                              <Stack direction="row" spacing={1}>
                                <Chip label={automation.label} size="small" color={automation.color} />
                                <Chip
                                  label={isConfigured ? 'Configured' : 'Not Configured'}
                                  size="small"
                                  color={isConfigured ? 'success' : 'warning'}
                                  variant={isConfigured ? 'filled' : 'outlined'}
                                />
                              </Stack>
                            </Box>
                            {isChecked && !isConfigured && (
                              <Typography variant="body2" color={theme.palette.warning.main} sx={{ mt: 1 }}>
                                Required credentials missing. Settings me jaake configure karo.
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          size="large"
          disabled={publishing}
          onClick={handleSubmit}
          sx={{ minWidth: 220 }}
        >
          {publishing ? 'Publishing...' : 'Publish to Selected Platforms'}
        </Button>
      </Box>

      {publishError && (
        <Alert severity="error" sx={{ mb: 2 }}>{publishError}</Alert>
      )}

      {summary && (
        <Alert severity={summary.failed > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
          Requested: {summary.requested} | Published: {summary.published} | Failed: {summary.failed} | Skipped: {summary.skipped}
        </Alert>
      )}

      {publishResults.length > 0 && (
        <Card sx={{ borderRadius: 3, border: `1px solid ${borderColor}`, boxShadow: sectionShadow }}>
          <CardContent>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Publish Results</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Platform</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Message</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Link</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {publishResults.map((result) => (
                    <TableRow key={result.platformId}>
                      <TableCell>{result.platformName}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={result.status}
                          color={result.status === 'published' ? 'success' : result.status === 'failed' ? 'error' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{result.message}</TableCell>
                      <TableCell>
                        {result.externalUrl ? (
                          <Link href={result.externalUrl} target="_blank" rel="noreferrer">
                            Open
                          </Link>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ContentSyndicationPanel;
